import { DOC_TYPE, SupplementStatus } from '@/api/additionalInfo'

export const DEFAULT_DOCUMENT_TYPES = Object.freeze([
  DOC_TYPE.FAMILY_CARD,
  DOC_TYPE.SOCIAL_MEDIA,
  DOC_TYPE.BANK_STATEMENT,
  DOC_TYPE.PAYSLIP,
  DOC_TYPE.DRIVER_LICENSE_FRONT,
  DOC_TYPE.DRIVER_LICENSE_BACK,
  DOC_TYPE.BPJS_TK_FRONT,
  DOC_TYPE.BPJS_TK_BACK
])

export const PERSONAL_ADDITIONAL_DISPLAY_STATE = Object.freeze({
  EDITING: 'editing',
  READY_TO_SUBMIT: 'ready-to-submit',
  UNDER_REVIEW: 'under-review',
  APPROVED: 'approved',
  RESUBMIT: 'resubmit'
})

const normalizeDocumentType = type => `${type || ''}`.trim().toUpperCase()

export function normalizeSupplementStatus(status) {
  return `${status || ''}`.trim().toUpperCase() || SupplementStatus.UNSUBMIT
}

export function isSupplementUnsubmit(status) {
  return normalizeSupplementStatus(status) === SupplementStatus.UNSUBMIT
}

export function isSupplementResubmit(status) {
  return normalizeSupplementStatus(status) === SupplementStatus.RESUBMIT
}

export function isSupplementSubmitted(status) {
  return normalizeSupplementStatus(status) === SupplementStatus.SUBMITTED
}

export function isSupplementApproved(status) {
  return normalizeSupplementStatus(status) === SupplementStatus.APPROVED
}

export function isSupplementEditableStatus(status) {
  return isSupplementUnsubmit(status) || isSupplementResubmit(status)
}

export function isSupplementUploaded(document = {}) {
  return !isSupplementUnsubmit(document.status) || !!document.ossKey
}

function normalizeDocument(document = {}, fallbackType = '') {
  const normalized = {
    ...document,
    type: normalizeDocumentType(document.type || fallbackType),
    status: normalizeSupplementStatus(document.status),
    ossKey: document.ossKey || ''
  }
  return {
    ...normalized,
    uploaded: isSupplementUploaded(normalized)
  }
}

export function normalizeSupplementDocuments(items = []) {
  const normalizedItems = Array.isArray(items)
    ? items.map(item => normalizeDocument(item)).filter(item => item.type)
    : []
  const remoteByType = new Map(normalizedItems.map(item => [item.type, item]))
  const ordered = DEFAULT_DOCUMENT_TYPES.map(type => remoteByType.get(type) || normalizeDocument({}, type))
  const extras = normalizedItems.filter(item => !DEFAULT_DOCUMENT_TYPES.includes(item.type))
  return ordered.concat(extras)
}

export function normalizeRequiredSupplementTypes(value) {
  let values = value
  if (typeof values === 'string') {
    try {
      values = JSON.parse(values)
    } catch (e) {
      values = values.split(',')
    }
  }
  if (!Array.isArray(values)) return []
  return Array.from(new Set(values.map(normalizeDocumentType).filter(Boolean)))
}

export function resolveRequiredSupplementTypes(features = {}) {
  const normalizedFeatures = features?.features || features || {}
  const requiredTypes = normalizeRequiredSupplementTypes(normalizedFeatures.subAdditionItems)
  return requiredTypes.length ? requiredTypes : [DOC_TYPE.FAMILY_CARD]
}

export function satisfiesRequiredUpload(document = {}) {
  return isSupplementSubmitted(document.status) ||
    isSupplementApproved(document.status) ||
    (isSupplementUnsubmit(document.status) && !!document.ossKey)
}

export function hasRequiredSupplementUploads(documents = [], requiredTypes = [DOC_TYPE.FAMILY_CARD]) {
  return requiredTypes.every(requiredType => {
    const normalizedType = normalizeDocumentType(requiredType)
    const document = documents.find(item => normalizeDocumentType(item.type) === normalizedType)
    return !!document && satisfiesRequiredUpload(document)
  })
}

export function hasNewSupplementUpload(documents = []) {
  return documents.some(document =>
    isSupplementUploaded(document) && isSupplementUnsubmit(document.status) && !!document.ossKey
  )
}

export function hasValidDriverLicensePair(documents = []) {
  const front = documents.find(item => normalizeDocumentType(item.type) === DOC_TYPE.DRIVER_LICENSE_FRONT)
  const back = documents.find(item => normalizeDocumentType(item.type) === DOC_TYPE.DRIVER_LICENSE_BACK)
  return isSupplementUploaded(front) === isSupplementUploaded(back)
}

export function isBpjsTkType(type) {
  return [DOC_TYPE.BPJS_TK_FRONT, DOC_TYPE.BPJS_TK_BACK].includes(normalizeDocumentType(type))
}

export function hasValidBpjsTkPair(documents = []) {
  const front = documents.find(item => normalizeDocumentType(item.type) === DOC_TYPE.BPJS_TK_FRONT)
  const back = documents.find(item => normalizeDocumentType(item.type) === DOC_TYPE.BPJS_TK_BACK)
  return isSupplementUploaded(front) === isSupplementUploaded(back)
}

export function canSkipOrderAdditional(statusData = {}, requiredTypes = [DOC_TYPE.FAMILY_CARD]) {
  if (isSupplementApproved(statusData.overallStatus) || isSupplementSubmitted(statusData.overallStatus)) {
    return true
  }
  const items = statusData.items || statusData.documents || []
  return hasRequiredSupplementUploads(normalizeSupplementDocuments(items), requiredTypes)
}

export function resolvePersonalAdditionalDisplayState(overallStatus, documents = [], basicVerified = false) {
  if (!basicVerified) return PERSONAL_ADDITIONAL_DISPLAY_STATE.EDITING
  if (hasNewSupplementUpload(documents)) return PERSONAL_ADDITIONAL_DISPLAY_STATE.READY_TO_SUBMIT
  if (documents.length && documents.every(document => isSupplementApproved(document.status))) {
    return PERSONAL_ADDITIONAL_DISPLAY_STATE.APPROVED
  }
  if (documents.length && documents.every(document => isSupplementSubmitted(document.status))) {
    return PERSONAL_ADDITIONAL_DISPLAY_STATE.UNDER_REVIEW
  }
  if (isSupplementResubmit(overallStatus) || documents.some(document => isSupplementResubmit(document.status))) {
    return PERSONAL_ADDITIONAL_DISPLAY_STATE.RESUBMIT
  }
  return PERSONAL_ADDITIONAL_DISPLAY_STATE.EDITING
}

export function buildSupplementSubmitItems(documents = []) {
  return documents
    .filter(document => isSupplementUnsubmit(document.status) && !!document.ossKey)
    .map(document => ({ type: document.type, ossKey: document.ossKey }))
}
